"use client"
import { useState } from "react"
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material"
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People,
  PersonAdd,
  CalendarToday,
  Receipt,
  LocalPharmacy,
  Science,
  Inventory,
  Assessment,
  AccountCircle,
  Logout,
  LocalHospital,
  AccountBalance,
} from "@mui/icons-material"

// Import components
import DashboardHome from "./DashboardHome"
import PatientManagement from "./PatientManagement"
import AppointmentManagement from "./AppointmentManagement"
import BillingManagement from "./BillingManagement"
import PharmacyManagement from "./PharmacyManagement"
import LabManagement from "./LabManagement"
import InventoryManagement from "./InventoryManagement"
import ReportsAnalytics from "./ReportsAnalytics"
import StaffManagement from "./StaffManagement"

const drawerWidth = 240

const getMenuItems = (userType) => {
  const commonItems = [{ text: "Dashboard", icon: <DashboardIcon />, component: "dashboard" }]

  const menuItems = {
    admin: [
      ...commonItems,
      { text: "Patient Management", icon: <People />, component: "patients" },
      { text: "Staff Management", icon: <PersonAdd />, component: "staff" },
      { text: "Appointments", icon: <CalendarToday />, component: "appointments" },
      { text: "Billing", icon: <Receipt />, component: "billing" },
      { text: "Pharmacy", icon: <LocalPharmacy />, component: "pharmacy" },
      { text: "Laboratory", icon: <Science />, component: "lab" },
      { text: "Inventory", icon: <Inventory />, component: "inventory" },
      { text: "Reports & Analytics", icon: <Assessment />, component: "reports" },
    ],
    doctor: [
      ...commonItems,
      { text: "My Patients", icon: <People />, component: "patients" },
      { text: "Appointments", icon: <CalendarToday />, component: "appointments" },
      { text: "Lab Reports", icon: <Science />, component: "lab" },
    ],
    reception: [
      ...commonItems,
      { text: "Patient Registration", icon: <PersonAdd />, component: "patients" },
      { text: "Appointments", icon: <CalendarToday />, component: "appointments" },
      { text: "Billing", icon: <Receipt />, component: "billing" },
    ],
    pharmacy: [
      ...commonItems,
      { text: "Pharmacy Management", icon: <LocalPharmacy />, component: "pharmacy" },
      { text: "Inventory", icon: <Inventory />, component: "inventory" },
    ],
    lab: [
      ...commonItems,
      { text: "Lab Management", icon: <Science />, component: "lab" },
      { text: "Reports", icon: <Assessment />, component: "reports" },
    ],
    billing: [
      ...commonItems,
      { text: "Billing Management", icon: <Receipt />, component: "billing" },
      { text: "Financial Reports", icon: <AccountBalance />, component: "reports" },
    ],
    staff: [
      ...commonItems,
      { text: "Patient Management", icon: <People />, component: "patients" },
      { text: "Appointments", icon: <CalendarToday />, component: "appointments" },
    ],
    patient: [
      ...commonItems,
      { text: "My Appointments", icon: <CalendarToday />, component: "appointments" },
      { text: "My Bills", icon: <Receipt />, component: "billing" },
      { text: "Lab Reports", icon: <Science />, component: "lab" },
    ],
  }

  return menuItems[userType] || commonItems
}

export default function Dashboard({ user, onLogout }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [activeComponent, setActiveComponent] = useState("dashboard")
  const [anchorEl, setAnchorEl] = useState(null)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const menuItems = getMenuItems(user.userType)

  const renderComponent = () => {
    switch (activeComponent) {
      case "dashboard":
        return <DashboardHome user={user} />
      case "patients":
        return <PatientManagement user={user} />
      case "staff":
        return <StaffManagement user={user} />
      case "appointments":
        return <AppointmentManagement user={user} />
      case "billing":
        return <BillingManagement user={user} />
      case "pharmacy":
        return <PharmacyManagement user={user} />
      case "lab":
        return <LabManagement user={user} />
      case "inventory":
        return <InventoryManagement user={user} />
      case "reports":
        return <ReportsAnalytics user={user} />
      default:
        return <DashboardHome user={user} />
    }
  }

  const drawer = (
    <div>
      <Toolbar>
        <LocalHospital sx={{ mr: 1, color: "primary.main" }} />
        <Typography variant="h6" noWrap component="div" color="primary">
          HMS
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={activeComponent === item.component}
              onClick={() => setActiveComponent(item.component)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  )

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Hospital Management System
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>{user.name.charAt(0)}</Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleProfileMenuClose}>
            <MenuItem onClick={handleProfileMenuClose}>
              <AccountCircle sx={{ mr: 1 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={onLogout}>
              <Logout sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
        <Toolbar />
        {renderComponent()}
      </Box>
    </Box>
  )
}
